import { applyParticipantUpdate, preserveChatParticipants } from "@/features/chat/chat-cache";
import { useScriptStore } from "@/features/worktree/scriptStore";
import { create } from "zustand";
import { flushSync } from "react-dom";
import { boundedClear, clearAccessScopedState } from "./access-reset";
import { markAccessRefreshing, observeAccessVersion } from "./access-version";
import { client } from "./api";
import {
  ClientToServer,
  isAssistantWorkspace,
  withProtocolVersion,
  type OrgUpdate,
  type PresenceUser,
  type ProtocolReady,
  type Repo,
  type Worktree,
  type WorktreeDetailResponse,
  newId,
  RivetsApiError,
} from "@rivets/core";
import { decodeServerFrame } from "./frames";
import { chatRefusal, deriveConnectionState, reportConnection, reportRelayOpen } from "./connectionState";
import { recordDiagnostic, registerDiagnosticSection } from "./diagnostics";
import { flushDrafts, forgetDraftsForChats, prepareForReload } from "@/features/chat/draftPersistence";
import { hasUnsavedFileDrafts } from "@/features/files/fileDraftStore";
import { setPendingSendRecovery, usePendingSends } from "@/features/chat/pendingSends";
import { useChatComposers } from "@/features/chat/chatComposerStore";
import { forgetCachedChats, setChatOwnerLookup } from "@/features/chat/chatPersistence";
import { TerminalControlFrame } from "@rivets/core/terminal-protocol";
import { queryClient } from "./query-client";
import { qk } from "./query-keys";
import { useChatStore } from "@/features/chat/chatStore";
import { clearStopping, confirmChatStatus } from "@/features/chat/optimisticStop";
import { applySnapshotUpdated } from "@/features/worktree/snapshotCache";
import { isLocallyWatched, workerCameOnline } from "./dataSource";
import { revalidateBlockedApprovals } from "@/features/chat/taskApproval";
import { applyWorkerUpdate, removeWorker, type WorkerListCache } from "@/features/workers/worker-cache";
import { parseTerminalBinaryFrame, parseTerminalV2Frame, type TerminalPayloadFrame } from "@/features/worktree/terminalProtocol";

export type ConnStatus = "idle" | "connecting" | "open" | "closed" | "upgrade-required";
export type TerminalSocketFrame = Extract<import("@rivets/core").ServerToClient, { type: "error" }> | TerminalPayloadFrame;

type TerminalFrameListener = (frame: TerminalSocketFrame) => void;

const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30_000;
const HEALTHY_AFTER_MS = 30_000;
/** How often the heartbeat looks; a ping goes out every `PING_EVERY_MS`. */
const HEARTBEAT_TICK_MS = 5_000;
export const PING_EVERY_MS = 10_000;
/** CONTRACT C2: the socket is declared dead after at most two missed pongs,
 * 25 s in all — nothing inbound for that long while a ping has gone
 * unanswered past `PONG_TIMEOUT_MS`. The server answers every ping, so that
 * silence means the flow is gone (VPN re-key, NAT rebind) even though the
 * browser still reports the socket open. Same numbers as the native client. */
export const INBOUND_SILENCE_MS = 25_000;
export const PONG_TIMEOUT_MS = 5_000;
/** A tab hidden at least this long proves its socket when it comes back. */
const VISIBLE_PROBE_AFTER_MS = 10_000;
const TYPING_THROTTLE_MS = 2000;

let ws: WebSocket | null = null;
let attempt = 0;
let healthyTimer: ReturnType<typeof setTimeout> | null = null;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let protocolTimer: ReturnType<typeof setTimeout> | null = null;
let probeTimer: ReturnType<typeof setTimeout> | null = null;
let explicitClose = false;
let lastInboundAt = 0;
let lastPingAt = 0;
/** Frames received on the current socket, so a probe can tell whether anything
 * answered it. */
let inboundCount = 0;
/** Whether this org's socket has ever completed a handshake on this page: a
 * socket down after one is "reconnecting", before one "connecting". */
let everConnected = false;
let currentOrgId: string | null = null;
let lastOrgRevision: number | null = null;
/** Whether this org's socket has completed a handshake before. The first
 * `protocol.ready` after a page load (or an org switch) finds every query it
 * could invalidate still in flight or just answered, so it invalidates
 * nothing; only a reconnect can have missed updates. */
let handshakeCompleted = false;
/** Chats subscribed on this socket. Their resume point is the chat store's
 * contiguity cursor, read at send time, so a reconnect resumes exactly where
 * coverage says history is complete. */
const subscriptions = new Set<string>();
const lastTypingSentAt = new Map<string, number>();
/** Pending tail checks per subscribed chat, and the `lastSeq` each last
 * acted on (see `checkChatTail`). */
const chatTailChecks = new Map<string, ReturnType<typeof setTimeout>>();
const chatTailCheckedAt = new Map<string, number>();
/** Grace before a tail check, then the waits between its retries. */
export const CHAT_TAIL_CHECK_DELAYS_MS = [2_000, 10_000, 30_000];
/** Subscribe retries after `chat.subscribe_failed` (CONTRACT P4), per chat. */
const subscribeRetries = new Map<string, { attempt: number; timer: ReturnType<typeof setTimeout> | null }>();
/** Subscribe retries: 1 s doubling to 30 s, jittered. Mutable for tests. */
export const SUBSCRIBE_RETRY_MS = { base: 1_000, max: 30_000 };
/** Live-gap repairs (web #4): a resubscribe from the cursor, debounced so the
 * frames still in flight behind the gap can land first, and backed off when a
 * repair does not close it. */
const gapRepairs = new Map<string, { attempt: number; timer: ReturnType<typeof setTimeout> | null }>();
/** The first repair waits this long; repeats double it up to `max`. Mutable for tests. */
export const GAP_REPAIR_MS = { delay: 400, max: 30_000 };
const terminalFrameListeners = new Set<TerminalFrameListener>();

type SetOrgSocketState = (
  partial: Partial<OrgSocketState> | ((s: OrgSocketState) => Partial<OrgSocketState>),
) => void;

interface OrgSocketState {
  status: ConnStatus;
  accessReloading: boolean;
  presenceByChat: Record<string, PresenceUser[]>;
  protocol?: ProtocolReady;
  protocolError?: string;
  connect: (orgId: string) => void;
  disconnect: () => void;
  subscribeChat: (chatId: string) => void;
  unsubscribeChat: (chatId: string) => void;
  sendTyping: (chatId: string) => void;
}

export const useOrgSocket = create<OrgSocketState>((set, get) => ({
  status: "idle",
  accessReloading: false,
  presenceByChat: {},
  protocol: undefined,
  protocolError: undefined,

  connect: (orgId) => {
    if (get().accessReloading) return;
    installSystemListeners();
    if (currentOrgId === orgId && ws) return;
    if (currentOrgId !== null && currentOrgId !== orgId) {
      // Switching orgs (not just an initial connect/reconnect) — every
      // chat/typing/presence entry we've accumulated belongs to the org
      // we're leaving, so drop it all rather than let it leak forever.
      clearAllOrgScopedState(set);
      detach(ws);
      ws?.close(1000, "organization changed");
      ws = null;
      everConnected = false;
    }
    explicitClose = false;
    set({ protocol: undefined, protocolError: undefined });
    currentOrgId = orgId;
    attempt = 0;
    clearTimers();
    openSocket(set, get);
  },

  disconnect: () => {
    explicitClose = true;
    currentOrgId = null;
    everConnected = false;
    clearAllOrgScopedState(set);
    clearTimers();
    detach(ws);
    ws?.close();
    ws = null;
    setStatus(set, "idle");
  },

  subscribeChat: (chatId) => {
    subscriptions.add(chatId);
    sendSubscribe(chatId);
  },

  unsubscribeChat: (chatId) => {
    subscriptions.delete(chatId);
    cancelChatTailCheck(chatId);
    cancelChatRepairs(chatId);
    lastTypingSentAt.delete(chatId);
    set((s) => {
      if (!(chatId in s.presenceByChat)) return s;
      const presenceByChat = { ...s.presenceByChat };
      delete presenceByChat[chatId];
      return { presenceByChat };
    });
    send({ type: "unsubscribe.chat", chatId });
  },

  sendTyping: (chatId) => {
    const now = Date.now();
    const last = lastTypingSentAt.get(chatId) ?? 0;
    if (now - last < TYPING_THROTTLE_MS) return;
    lastTypingSentAt.set(chatId, now);
    send({ type: "typing", chatId });
  },
}));

/** Drop every chat/typing/presence entry accumulated for the current org.
 * Called on explicit disconnect and on org switch so stale per-chat state
 * (and the chatStore's per-chat event buffers) don't accumulate forever. */
function clearAllOrgScopedState(set: SetOrgSocketState) {
  lastOrgRevision = null;
  handshakeCompleted = false;
  subscriptions.clear();
  clearChatTailChecks();
  clearAllChatRepairs();
  clearStopping();
  lastTypingSentAt.clear();
  set({ presenceByChat: {} });
  useChatStore.getState().resetAll();
}

function clearTimers() {
  if (healthyTimer) clearTimeout(healthyTimer);
  if (heartbeatTimer) clearInterval(heartbeatTimer);
  if (reconnectTimer) clearTimeout(reconnectTimer);
  if (protocolTimer) clearTimeout(protocolTimer);
  if (probeTimer) clearTimeout(probeTimer);
  healthyTimer = heartbeatTimer = reconnectTimer = protocolTimer = probeTimer = null;
}

/** Every status change goes through here, so the app's connection state
 * (CONTRACT C1) follows the socket exactly. */
function setStatus(set: SetOrgSocketState, status: ConnStatus, extra: Partial<OrgSocketState> = {}) {
  set({ ...extra, status });
  if (status === "open") everConnected = true;
  reportRelayOpen(status === "open");
  reportConnection(deriveConnectionState({ socket: status, online: browserOnline(), everConnected }));
}

function browserOnline(): boolean {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

/** The socket's handlers are cut before it is closed: a dead flow's `onclose`
 * can take a minute to arrive, and nothing it says matters any more. */
function detach(socket: WebSocket | null) {
  if (!socket) return;
  socket.onopen = null;
  socket.onmessage = null;
  socket.onclose = null;
  socket.onerror = null;
}

/**
 * Gives up on the current socket now, rather than waiting for its `onclose`
 * (web #5): on a half-open flow that wait is the browser's closing-handshake
 * timeout, about a minute, during which the app still read "Live".
 */
function abandonSocket(set: SetOrgSocketState, code: number, reason: string, options: { resetBackoff?: boolean } = {}) {
  const socket = ws;
  if (!socket) return;
  console.warn(`[org-socket] ${reason}; reconnecting`);
  recordDiagnostic("socket", reason);
  ws = null;
  detach(socket);
  if (healthyTimer) clearTimeout(healthyTimer);
  if (heartbeatTimer) clearInterval(heartbeatTimer);
  if (protocolTimer) clearTimeout(protocolTimer);
  if (probeTimer) clearTimeout(probeTimer);
  healthyTimer = heartbeatTimer = protocolTimer = probeTimer = null;
  try { socket.close(code, reason); } catch { /* already closing */ }
  if (explicitClose) return;
  if (options.resetBackoff) attempt = 0;
  setStatus(set, "closed");
  scheduleReconnect(set);
}

/**
 * What the heartbeat does on a tick. Dead needs both: nothing inbound for
 * `INBOUND_SILENCE_MS`, and a ping sent after the last inbound frame that has
 * gone `PONG_TIMEOUT_MS` unanswered. A background tab whose timers Chrome
 * throttles to once a minute is quiet that long without being dead — its
 * pongs still arrive — so silence alone must not condemn it (web #17).
 */
export function heartbeatVerdict(input: { now: number; lastInboundAt: number; lastPingAt: number }): "dead" | "ping" | "wait" {
  const { now, lastInboundAt, lastPingAt } = input;
  const unanswered = lastPingAt > lastInboundAt && now - lastPingAt >= PONG_TIMEOUT_MS;
  if (unanswered && now - lastInboundAt >= INBOUND_SILENCE_MS) return "dead";
  if (now - lastPingAt >= PING_EVERY_MS) return "ping";
  return "wait";
}

/**
 * CONTRACT C2: on a network change, or the tab coming back after being hidden,
 * prove the socket now — a ping answered within 5 s — instead of trusting it
 * until the heartbeat notices. One that does not answer is abandoned and
 * reconnected with the backoff reset; a reconnect waiting out its backoff
 * goes now.
 */
export function probeOrgSocket(): void {
  const set = useOrgSocket.setState as SetOrgSocketState;
  if (explicitClose || !currentOrgId) return;
  const socket = ws;
  if (!socket || useOrgSocket.getState().status !== "open") {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
      attempt = 0;
      openSocket(set, useOrgSocket.getState);
    }
    return;
  }
  if (probeTimer) return;
  const answeredBy = inboundCount;
  lastPingAt = Date.now();
  try { socket.send(PING_FRAME); } catch { /* the deadline below decides */ }
  probeTimer = setTimeout(() => {
    probeTimer = null;
    if (ws === socket && inboundCount === answeredBy) abandonSocket(set, 4004, "probe unanswered", { resetBackoff: true });
  }, PONG_TIMEOUT_MS);
}

let systemListenersInstalled = false;
let hiddenSince: number | null = null;

/** Network and visibility (CONTRACT C2). Installed once, on the first connect. */
function installSystemListeners() {
  if (systemListenersInstalled || typeof window === "undefined" || typeof window.addEventListener !== "function") return;
  systemListenersInstalled = true;
  window.addEventListener("online", () => {
    reportOnlineChange();
    probeOrgSocket();
  });
  window.addEventListener("offline", reportOnlineChange);
  if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        hiddenSince = Date.now();
        return;
      }
      const hiddenFor = hiddenSince === null ? 0 : Date.now() - hiddenSince;
      hiddenSince = null;
      if (hiddenFor >= VISIBLE_PROBE_AFTER_MS) probeOrgSocket();
    });
  }
}

function reportOnlineChange() {
  setStatus(useOrgSocket.setState as SetOrgSocketState, useOrgSocket.getState().status);
}

function send(frame: ClientToServer) {
  sendRaw(frame);
}

/** Replay is bounded: a chat that was away for longer than this resumes from
 * its newest events and pages the rest with `beforeSeq` on scroll-up. */
export const CHAT_SUBSCRIBE_MAX_REPLAY = 2_000;

function sendSubscribe(chatId: string) {
  const afterSeq = useChatStore.getState().chats[chatId]?.cursor ?? 0;
  if (sendRaw({ type: "subscribe.chat", chatId, afterSeq, maxReplay: CHAT_SUBSCRIBE_MAX_REPLAY } satisfies ClientToServer)) {
    useChatStore.getState().setSync(chatId, "synchronizing");
  }
}

/** Jittered backoff, `base` doubling per attempt up to `max`, in [½, 1) of it. */
function backoffDelay(base: number, max: number, attemptIndex: number): number {
  const cap = Math.min(max, base * 2 ** attemptIndex);
  return cap * (0.5 + Math.random() * 0.5);
}

/**
 * The server could not replay a subscription (CONTRACT P4): the chat stays
 * unsynced — no presence came, so it never read "live" — and the subscribe
 * is retried, 1 s doubling to 30 s with jitter, while the server says a retry
 * can work.
 */
function scheduleSubscribeRetry(chatId: string) {
  const state = subscribeRetries.get(chatId) ?? { attempt: 0, timer: null };
  if (state.timer) clearTimeout(state.timer);
  const delay = backoffDelay(SUBSCRIBE_RETRY_MS.base, SUBSCRIBE_RETRY_MS.max, state.attempt);
  state.attempt += 1;
  state.timer = setTimeout(() => {
    state.timer = null;
    if (subscriptions.has(chatId)) sendSubscribe(chatId);
  }, delay);
  subscribeRetries.set(chatId, state);
}

/** Whether the held stream has events past its cursor that a missing seq
 * keeps it from walking over: a live frame was lost in the middle. */
function hasLiveGap(chatId: string): boolean {
  const stream = useChatStore.getState().chats[chatId];
  if (!stream?.loaded) return false;
  const last = stream.events[stream.events.length - 1];
  return last !== undefined && last.seq > stream.cursor;
}

/**
 * A live frame went missing mid-stream (web #4): a tool card that never
 * completes, an answer cut to its deltas. Nothing else would ask for it until
 * the socket happened to drop, so the chat resubscribes from its cursor —
 * after a short wait for frames still in flight, and backing off if repairs
 * keep finding the gap (a server that never fills it must not be asked in a
 * loop).
 */
function scheduleGapRepair(chatId: string) {
  if (!subscriptions.has(chatId)) return;
  const state = gapRepairs.get(chatId) ?? { attempt: 0, timer: null };
  if (state.timer) return;
  const delay = Math.min(GAP_REPAIR_MS.max, GAP_REPAIR_MS.delay * 2 ** state.attempt);
  state.timer = setTimeout(() => {
    state.timer = null;
    if (!subscriptions.has(chatId) || !hasLiveGap(chatId)) {
      gapRepairs.delete(chatId);
      return;
    }
    state.attempt += 1;
    recordDiagnostic("gap", `chat ${chatId}: resubscribing from ${useChatStore.getState().chats[chatId]?.cursor ?? 0}`);
    sendSubscribe(chatId);
  }, delay);
  gapRepairs.set(chatId, state);
}

function cancelChatRepairs(chatId: string) {
  const retry = subscribeRetries.get(chatId);
  if (retry?.timer) clearTimeout(retry.timer);
  subscribeRetries.delete(chatId);
  const repair = gapRepairs.get(chatId);
  if (repair?.timer) clearTimeout(repair.timer);
  gapRepairs.delete(chatId);
}

function clearAllChatRepairs() {
  for (const chatId of [...subscribeRetries.keys(), ...gapRepairs.keys()]) cancelChatRepairs(chatId);
}

/** The highest seq this client holds or knows to be covered (the server may
 * elide seqs inside a replay's coverage). */
function newestHeldSeq(chatId: string): number {
  const stream = useChatStore.getState().chats[chatId];
  return stream ? Math.max(stream.lastSeq, stream.cursor) : 0;
}

/**
 * A chat record's `lastSeq` is the highest seq the server's log holds, and
 * `chat.updated` reaches this socket after that event's own frame. Holding
 * less means a live frame went missing on the way. One lost mid-turn leaves a
 * hole the next replay fills; one lost at the end of a turn has nothing after
 * it, and the chat sat on a running turn until it was reopened.
 * Resubscribing from the cursor replays whatever is missing. Seqs, not
 * timestamps: events share milliseconds and worker clocks skew, so a
 * timestamp both missed real gaps and chased ones that were not there. The
 * native client runs the same check (`AppModel.checkChatTail`).
 */
function checkChatTail(chatId: string, serverNewest: number | null | undefined) {
  if (!serverNewest || !subscriptions.has(chatId)) return;
  if ((chatTailCheckedAt.get(chatId) ?? 0) >= serverNewest || newestHeldSeq(chatId) >= serverNewest) return;
  chatTailCheckedAt.set(chatId, serverNewest);
  const attempt = (index: number) => {
    const delay = CHAT_TAIL_CHECK_DELAYS_MS[index];
    if (delay === undefined) { chatTailChecks.delete(chatId); return; }
    chatTailChecks.set(chatId, setTimeout(() => {
      // The grace covers a frame that is merely still being applied; the
      // retries, a replay that itself failed.
      if (!subscriptions.has(chatId) || newestHeldSeq(chatId) >= serverNewest) { chatTailChecks.delete(chatId); return; }
      sendSubscribe(chatId);
      attempt(index + 1);
    }, delay));
  };
  cancelChatTailCheck(chatId, { keepStamp: true });
  attempt(0);
}

function cancelChatTailCheck(chatId: string, options: { keepStamp?: boolean } = {}) {
  const timer = chatTailChecks.get(chatId);
  if (timer) clearTimeout(timer);
  chatTailChecks.delete(chatId);
  if (!options.keepStamp) chatTailCheckedAt.delete(chatId);
}

function clearChatTailChecks() {
  for (const timer of chatTailChecks.values()) clearTimeout(timer);
  chatTailChecks.clear();
  chatTailCheckedAt.clear();
}

/** The constant ping: OrgHub answers it without waking
 * (`setWebSocketAutoResponse`), which only works for one exact string. */
export const PING_FRAME = JSON.stringify({ type: "ping", t: 0 } satisfies ClientToServer);

function sendRaw(frame: unknown): boolean {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(frame));
    return true;
  }
  return false;
}

export function subscribeTerminalFrames(listener: TerminalFrameListener): () => void {
  terminalFrameListeners.add(listener);
  return () => terminalFrameListeners.delete(listener);
}

/** Ask the worker that owns this worktree to start a repository script. The
 * answer is the next `script.state`, never a direct reply. */
export function runScript(worktreeId: string, scriptId: string): void {
  send({ type: "script.run", worktreeId, scriptId });
}

export function stopScript(worktreeId: string, scriptId: string): void {
  send({ type: "script.stop", worktreeId, scriptId });
}

/** Ask the worker to republish a worktree's script set.
 *
 * `script.state` is broadcast live and never stored, so a client that opened
 * this workspace after the last one — or reconnected across it — has nothing,
 * or worse, a `running` badge for a script that finished while it was away. */
export function refreshScripts(worktreeId: string): void {
  send({ type: "script.refresh", worktreeId });
}

/** Terminal frames wait for `protocol.ready`, like every application frame:
 * the socket being transport-open says nothing about the server it reached. */
function sendTerminalV2(frame: import("@rivets/core/terminal-protocol").TerminalControlFrame): boolean {
  return useOrgSocket.getState().status === "open" && sendRaw(frame);
}

export function createTerminalV2(terminalId: string, worktreeId: string, cols: number, rows: number, program?: "claude" | "codex"): boolean {
  const parsed = TerminalControlFrame.parse({ type: "terminal.create", requestId: newId("req"), terminalId, worktreeId, cols, rows, program });
  return sendTerminalV2(parsed);
}

export function attachTerminalV2(terminalId: string, afterSeq: number, restore: "live" | "visible" | "full" = "visible"): boolean {
  const parsed = TerminalControlFrame.parse({ type: "terminal.attach", requestId: newId("req"), terminalId, afterSeq, restore });
  return sendTerminalV2(parsed);
}

export function detachTerminalV2(terminalId: string): boolean {
  const parsed = TerminalControlFrame.parse({ type: "terminal.detach", requestId: newId("req"), terminalId });
  return sendTerminalV2(parsed);
}

export function closeTerminalV2(terminalId: string): boolean {
  const parsed = TerminalControlFrame.parse({ type: "terminal.close", requestId: newId("req"), terminalId });
  return sendTerminalV2(parsed);
}

export function sendTerminalInputV2(terminalId: string, producerId: string, inputSeq: number, data: string): boolean {
  if (!data) return false;
  const parsed = TerminalControlFrame.parse({ type: "terminal.input", terminalId, producerId, inputSeq, data });
  return sendTerminalV2(parsed);
}

export function resizeTerminalV2(terminalId: string, leaseId: string, leaseEpoch: number, cols: number, rows: number): boolean {
  const parsed = TerminalControlFrame.parse({ type: "terminal.resize", terminalId, leaseId, leaseEpoch, cols, rows });
  return sendTerminalV2(parsed);
}

export function acknowledgeTerminalV2(terminalId: string, incarnation: number, appliedSeq: number, creditBytes: number): boolean {
  const parsed = TerminalControlFrame.parse({ type: "terminal.ack", terminalId, incarnation, appliedSeq, creditBytes });
  return sendTerminalV2(parsed);
}

function scheduleReconnect(set: SetOrgSocketState) {
  if (explicitClose || !currentOrgId) return;
  if (reconnectTimer) clearTimeout(reconnectTimer);
  const cap = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** attempt);
  const delay = Math.random() * cap;
  attempt++;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    openSocket(set, useOrgSocket.getState);
  }, delay);
}

function openSocket(
  set: SetOrgSocketState,
  get: () => OrgSocketState,
) {
  if (!currentOrgId) return;
  const orgId = currentOrgId;
  setStatus(set, "connecting");
  const url = withProtocolVersion(
    `${location.origin.replace(/^http/, "ws")}/api/ws/org/${orgId}`,
    "web",
  );
  const socket = new WebSocket(url);
  socket.binaryType = "arraybuffer";
  ws = socket;

  socket.onopen = () => {
    if (ws !== socket) return;
    // The socket is transport-open, but subscriptions wait until the server
    // confirms the exact current protocol version.
    protocolTimer = setTimeout(() => {
      if (ws === socket && get().status === "connecting") socket.close(4002, "protocol negotiation timed out");
    }, 10_000);
  };

  socket.onmessage = (event) => {
    if (ws !== socket) return;
    lastInboundAt = Date.now();
    inboundCount += 1;
    if (event.data instanceof ArrayBuffer) {
      const binary = parseTerminalBinaryFrame(event.data);
      if (!binary) return;
      if (get().status === "connecting") {
        socket.close(4002, "terminal data received before protocol negotiation");
        return;
      }
      for (const listener of terminalFrameListeners) listener(binary);
      return;
    }
    if (typeof event.data !== "string") return;
    const terminal = parseTerminalV2Frame(event.data);
    if (terminal) {
      if (get().status === "connecting") {
        socket.close(4002, "terminal frame received before protocol negotiation");
        return;
      }
      for (const listener of terminalFrameListeners) listener(terminal);
      return;
    }
    const decoded = decodeServerFrame(event.data);
    if (decoded.kind === "dropped") return;
    if (decoded.kind === "unreadable-org-update") {
      if (get().status === "open") handleUnreadableOrgUpdate(decoded.revision);
      return;
    }
    const frame = decoded.frame;
    if (get().status === "connecting" && frame.type !== "protocol.ready" && frame.type !== "protocol.incompatible") {
      console.error("[org-socket] application frame received before protocol negotiation", frame.type);
      socket.close(4002, "protocol not negotiated");
      return;
    }
    handleFrame(frame, set, get);
  };

  socket.onclose = (event) => {
    if (ws !== socket) return;
    recordDiagnostic("socket", `closed ${event?.code ?? "?"}${event?.reason ? ` (${event.reason})` : ""}${typeof document !== "undefined" && document.visibilityState === "hidden" ? " while hidden" : ""}`);
    const negotiationFailed = get().status === "connecting";
    if (healthyTimer) clearTimeout(healthyTimer);
    if (heartbeatTimer) clearInterval(heartbeatTimer);
    if (protocolTimer) clearTimeout(protocolTimer);
    if (probeTimer) clearTimeout(probeTimer);
    healthyTimer = heartbeatTimer = protocolTimer = probeTimer = null;
    ws = null;
    if (!explicitClose) {
      setStatus(set, "closed");
      scheduleReconnect(set);
      if (negotiationFailed) {
        // Browsers hide an upgrade's HTTP status. A removed membership may
        // never reach the hub's access.version handshake, so check admission
        // through REST before leaving an offline transcript on screen.
        void client.orgDetail(orgId).catch((error: unknown) => {
          if (currentOrgId === orgId && get().status !== "open" &&
            error instanceof RivetsApiError && [401, 403, 404].includes(error.status)) leaveInaccessibleOrg(error.status === 401);
        });
      }
    }
  };

  socket.onerror = () => {
    socket.close();
  };
}

function handleFrame(
  frame: import("@rivets/core").ServerToClient,
  set: SetOrgSocketState,
  get: () => OrgSocketState,
) {
  switch (frame.type) {
    case "protocol.ready": {
      if (frame.orgRevision === undefined) {
        console.error("[org-socket] protocol.ready omitted the org revision baseline");
        socketCloseForInvalidProtocol();
        return;
      }
      if (protocolTimer) clearTimeout(protocolTimer);
      protocolTimer = null;
      const previousRevision = lastOrgRevision;
      lastOrgRevision = frame.orgRevision;
      if (currentOrgId && shouldReconcileOnReady(handshakeCompleted, previousRevision, frame.orgRevision)) {
        reconcileOrgQueries(currentOrgId);
      }
      handshakeCompleted = true;
      setStatus(set, "open", { protocol: frame, protocolError: undefined });
      healthyTimer = setTimeout(() => { attempt = 0; }, HEALTHY_AFTER_MS);
      lastInboundAt = lastPingAt = Date.now();
      const socket = ws;
      heartbeatTimer = setInterval(() => {
        if (ws !== socket || !socket) return;
        const verdict = heartbeatVerdict({ now: Date.now(), lastInboundAt, lastPingAt });
        if (verdict === "dead") {
          abandonSocket(set, 4004, "no pong from the relay");
          return;
        }
        if (verdict === "ping" && socket.readyState === WebSocket.OPEN) {
          lastPingAt = Date.now();
          socket.send(PING_FRAME);
        }
      }, HEARTBEAT_TICK_MS);
      for (const chatId of subscriptions) sendSubscribe(chatId);
      return;
    }
    case "protocol.incompatible": {
      if (protocolTimer) clearTimeout(protocolTimer);
      protocolTimer = null;
      explicitClose = true;
      setStatus(set, "upgrade-required", { protocolError: frame.message });
      console.error("[org-socket] upgrade required", frame);
      recordDiagnostic("socket", `protocol.incompatible: expected ${frame.expectedVersion} (${frame.serverGeneration})`);
      const socket = ws;
      ws = null;
      detach(socket);
      socket?.close(4003, "upgrade required");
      reloadForUpdate(frame.expectedVersion);
      return;
    }
    case "chat.events": {
      // Replay frames carry the interval they scanned, which moves the
      // cursor over elided deltas; live frames never do.
      if (frame.coverage) {
        useChatStore.getState().applyCovered(frame.chatId, {
          events: frame.events,
          coverage: frame.coverage,
          // True for every replay frame after the first as well; that is
          // harmless, because `applyCoverage` only reads the flag when the
          // frame places or extends the bottom of the window, which only the
          // first frame of a replay can do.
          hasMoreBefore: frame.hasGapBefore === true || frame.coverage.fromSeq > 1,
        });
        // The repair this replay answers is done, or the next live frame
        // will say otherwise.
        const repair = gapRepairs.get(frame.chatId);
        if (repair && !repair.timer && !hasLiveGap(frame.chatId)) gapRepairs.delete(frame.chatId);
      } else if (frame.events.length > 0) {
        useChatStore.getState().mergeEvents(frame.chatId, frame.events);
        if (hasLiveGap(frame.chatId)) scheduleGapRepair(frame.chatId);
        else gapRepairs.delete(frame.chatId);
      }
      return;
    }
    case "presence": {
      if (!subscriptions.has(frame.chatId)) return;
      // OrgHub sends presence only once a subscribe's replay succeeded
      // (CONTRACT P4), so this is the point the stream on screen is the
      // relay's, not a cache — and a failed subscribe's retries can stop.
      const retry = subscribeRetries.get(frame.chatId);
      if (retry?.timer) clearTimeout(retry.timer);
      subscribeRetries.delete(frame.chatId);
      useChatStore.getState().setSync(frame.chatId, "live");
      set((s) => ({ presenceByChat: { ...s.presenceByChat, [frame.chatId]: frame.users } }));
      return;
    }
    case "chat.subscribe_failed": {
      console.warn("[org-socket] subscribe failed", frame.chatId, frame.code, frame.message);
      recordDiagnostic("subscribe", `chat ${frame.chatId}: ${frame.code} ${frame.message}${frame.retryable ? " (retrying)" : ""}`);
      if (!subscriptions.has(frame.chatId)) return;
      // Not live: the chat keeps saying so ("Catching up…") until a replay
      // succeeds.
      useChatStore.getState().setSync(frame.chatId, "synchronizing");
      if (frame.retryable) scheduleSubscribeRetry(frame.chatId);
      // Nothing will catch up: the chat says why (review #15).
      else useChatStore.getState().refuse(frame.chatId, chatRefusal(frame.code));
      return;
    }
    case "org.update": {
      if (lastOrgRevision !== null && frame.revision <= lastOrgRevision) return;
      const expected = lastOrgRevision === null ? null : lastOrgRevision + 1;
      lastOrgRevision = frame.revision;
      // Revocation cannot be skipped just because another frame was missed.
      if (frame.update.kind === "access.changed") {
        if (currentOrgId) void reconcileAccessChange(currentOrgId);
        return;
      }
      if (expected === null || frame.revision !== expected) {
        // A gap on a live socket: the same targeted reconcile as a reconnect.
        if (currentOrgId) reconcileOrgQueries(currentOrgId);
        // The update that was missed may have been the only sign an open
        // chat's tail frame was lost too. Resuming from the cursor costs
        // nothing when it was not.
        for (const chatId of subscriptions) sendSubscribe(chatId);
        return;
      }
      applyOrgUpdate(frame.update);
      return;
    }
    case "org.update.private": {
      // Delivered only to the member it belongs to (an assistant conversation),
      // so it carries no revision: applying it must not touch the org sequence,
      // which every other socket in this organization also has to observe.
      applyOrgUpdate(frame.update);
      return;
    }
    case "script.state":
      // Whole-set replacement, deliberately outside the org revision stream:
      // a dev server flipping state must not make every client reconcile.
      useScriptStore.getState().apply(frame.worktreeId, frame.scripts, frame.problems);
      return;
    case "pong":
      return;
    case "error":
      for (const listener of terminalFrameListeners) listener(frame);
      console.warn("[org-socket] server error", frame.code, frame.message, frame.chatId ?? "");
      recordDiagnostic("server-error", `${frame.code}: ${frame.message}${frame.chatId ? ` (chat ${frame.chatId})` : ""}`);
      return;
  }
}


function socketCloseForInvalidProtocol() {
  ws?.close(4002, "invalid protocol.ready");
}

/**
 * Whether a `protocol.ready` has to reconcile the org's REST caches.
 *
 * A page load's first handshake never does: every query is either still in
 * flight or was just answered, and invalidating them (TanStack cancels an
 * in-flight fetch to restart it) only doubled the cold-start traffic. After a
 * reconnect, only a revision gap means an update was missed.
 */
export function shouldReconcileOnReady(completedBefore: boolean, previousRevision: number | null, readyRevision: number): boolean {
  if (!completedBefore) return false;
  return previousRevision === null || readyRevision !== previousRevision;
}

/** A cached approval still says pending, but its chat has left the gate. */
export function approvalOutdated(
  approval: Pick<import("@rivets/core").TaskApproval, "status"> | null | undefined,
  chatStatus: import("@rivets/core").ChatStatus,
): boolean {
  return approval?.status === "pending" && chatStatus !== "awaiting_approval";
}

function reconcileOrgQueries(orgId: string) {
  // `cancelRefetch: false`: a read already in flight answers for this gap too.
  // Only observed queries refetch; the rest are marked stale for their next use.
  const options = { cancelRefetch: false } as const;
  void Promise.all([
    queryClient.invalidateQueries({ queryKey: qk.org(orgId) }, options),
    queryClient.invalidateQueries({ queryKey: qk.repos(orgId) }, options),
    queryClient.invalidateQueries({ queryKey: qk.workers(orgId) }, options),
    queryClient.invalidateQueries({ queryKey: qk.github(orgId) }, options),
    queryClient.invalidateQueries({ queryKey: qk.githubRepos(orgId) }, options),
    queryClient.invalidateQueries({ queryKey: ["worktrees"] }, options),
    queryClient.invalidateQueries({ queryKey: ["worktree"] }, options),
    queryClient.invalidateQueries({ queryKey: ["chat"] }, options),
    // A decision made while this socket was away moved the chat on; the card
    // must not keep offering Approve for it.
    queryClient.invalidateQueries({ queryKey: ["chat-approval"] }, options),
    queryClient.invalidateQueries({ queryKey: ["snapshot"] }, options),
  ]);
}

function applyOrgUpdate(update: OrgUpdate) {
  if (!currentOrgId) return;
  const orgId = currentOrgId;
  switch (update.kind) {
    case "org.scope.hidden":
      // Only the enclosing org revision is meaningful to this recipient.
      return;
    case "access.changed":
      void reconcileAccessChange(orgId);
      return;
    case "access.version": {
      // A revision this browser never stored is unknown, not changed
      // (CONTRACT C4): the first socket per organization only records it.
      if (observeAccessVersion(orgId, update.revision).changed) void reconcileAccessChange(orgId);
      return;
    }
    case "worktree.git.changed": {
      // A git operation finished after its request gave up (P3): what the
      // worktree's git state is now is read again rather than guessed.
      void queryClient.invalidateQueries({ queryKey: qk.snapshot(update.worktreeId) });
      void queryClient.invalidateQueries({ queryKey: qk.prWorkspace(update.worktreeId) });
      void queryClient.invalidateQueries({ queryKey: qk.pullRequestStatus(update.worktreeId) });
      void queryClient.invalidateQueries({ queryKey: qk.worktree(update.worktreeId) });
      return;
    }
    case "worker.updated": {
      const wasOnline = cachedWorkerOnline(orgId, update.worker.id);
      // Whole-worker replacement: a snapshot without `deliveryStall` clears
      // it, and the frame is tailored to this viewer, so its `canUse` and
      // grants replace the cached ones rather than merging over them.
      queryClient.setQueryData<WorkerListCache>(qk.workers(orgId), (old) => applyWorkerUpdate(old, update.worker));
      // Back online (a restart): the same-machine fast path comes back now.
      if (!wasOnline && update.worker.status === "online") {
        workerCameOnline(update.worker.id);
        revalidateBlockedApprovals(queryClient);
      }
      return;
    }
    case "worker.removed": {
      queryClient.setQueryData<WorkerListCache>(qk.workers(orgId), (old) => removeWorker(old, update.workerId));
      return;
    }
    case "worker.status": {
      const wasOnline = cachedWorkerOnline(orgId, update.workerId);
      queryClient.setQueryData<WorkerListCache>(
        qk.workers(orgId),
        (old) =>
          old
            ? {
                ...old,
                workers: old.workers.map((w) =>
                  w.id === update.workerId
                    ? { ...w, status: update.status, ...(update.capabilities ? { capabilities: update.capabilities } : {}) }
                    : w,
                ),
              }
            : old,
      );
      if (!wasOnline && update.status === "online") {
        workerCameOnline(update.workerId);
        revalidateBlockedApprovals(queryClient);
      }
      return;
    }
    case "repo.updated": {
      queryClient.setQueryData<{ repos: import("@rivets/core").Repo[] }>(
        qk.repos(orgId),
        (old) => old
          ? {
              repos: old.repos.some((repo) => repo.id === update.repo.id)
                ? old.repos.map((repo) => repo.id === update.repo.id ? update.repo : repo)
                : [...old.repos, update.repo],
            }
          : old,
      );
      return;
    }
    case "org.detail.changed": {
      void queryClient.invalidateQueries({ queryKey: qk.me() });
      void queryClient.invalidateQueries({ queryKey: qk.org(orgId) });
      return;
    }
    case "github.updated": {
      void queryClient.invalidateQueries({ queryKey: qk.github(orgId) });
      void queryClient.invalidateQueries({ queryKey: qk.githubRepos(orgId) });
      return;
    }
    case "chat.participants": {
      queryClient.setQueryData<string[]>(qk.worktreeParticipants(update.worktreeId),
        (old) => [...new Set([...(old ?? []), ...update.participantIds])]);
      queryClient.setQueryData<import("@rivets/core").WorktreeDetailResponse>(qk.worktree(update.worktreeId),
        (old) => applyParticipantUpdate(old, update));
      return;
    }
    case "chat.updated": {
      const chat = update.chat;
      confirmChatStatus(chat);
      checkChatTail(chat.id, chat.lastSeq);
      queryClient.setQueryData<import("@rivets/core").WorktreeDetailResponse>(
        qk.worktree(chat.worktreeId),
        (old) =>
          old
            ? {
                ...old,
                chats: old.chats.some((c) => c.id === chat.id)
                  ? old.chats.map((c) => (c.id === chat.id ? preserveChatParticipants(c, chat) : c))
                  : [chat, ...old.chats],
              }
            : old,
      );
      queryClient.setQueryData<import("@rivets/core").ChatDetailResponse>(
        qk.chat(chat.id),
        (old) => (old ? { ...old, chat } : old),
      );
      // Another member approved or rejected it: the cached pending approval
      // is stale, and refetching it turns the card into the decided banner.
      if (approvalOutdated(queryClient.getQueryData<import("@rivets/core").TaskApproval | null>(qk.chatApproval(chat.id)), chat.status)) {
        void queryClient.invalidateQueries({ queryKey: qk.chatApproval(chat.id) });
      }
      return;
    }
    case "worktree.updated": {
      const wt = update.worktree;
      // The assistant's scratch workspace is private to one member and is
      // excluded from every REST list; caching a realtime frame for it would
      // put it back into the sidebar and the command palette (FRNT-699).
      if (isAssistantWorkspace(wt.source)) return;
      queryClient.setQueryData<{ worktrees: import("@rivets/core").Worktree[] }>(
        qk.worktrees(wt.repoId),
        (old) =>
          old
            ? {
                worktrees: old.worktrees.some((w) => w.id === wt.id)
                  ? old.worktrees.map((w) => (w.id === wt.id ? wt : w))
                  : [wt, ...old.worktrees],
              }
            : old,
      );
      queryClient.setQueryData<import("@rivets/core").WorktreeDetailResponse>(
        qk.worktree(wt.id),
        (old) => (old ? { ...old, worktree: wt } : old),
      );
      return;
    }
    case "worktree.removed": {
      queryClient.setQueryData<{ worktrees: import("@rivets/core").Worktree[] }>(
        qk.worktrees(update.repoId),
        (old) => old ? { worktrees: old.worktrees.filter((worktree) => worktree.id !== update.worktreeId) } : old,
      );
      queryClient.removeQueries({ queryKey: qk.worktree(update.worktreeId) });
      queryClient.removeQueries({ queryKey: qk.snapshot(update.worktreeId) });
      queryClient.removeQueries({ queryKey: qk.prWorkspace(update.worktreeId) });
      queryClient.removeQueries({ queryKey: qk.files(update.worktreeId) });
      queryClient.removeQueries({ queryKey: qk.dirs(update.worktreeId) });
      return;
    }
    case "job.updated": {
      const job = update.job;
      queryClient.setQueryData<import("@rivets/core").ChatDetailResponse>(
        qk.chat(job.chatId),
        (old) =>
          old
            ? {
                ...old,
                jobs: old.jobs.some((j) => j.id === job.id)
                  ? old.jobs.map((j) => (j.id === job.id ? job : j))
                  : [job, ...old.jobs],
              }
            : old,
      );
      return;
    }
    case "snapshot.updated": {
      // The totals ride on the frame (sidebar, Home, overview need no fetch).
      // File content is cached indefinitely because it cannot change without a
      // new snapshot; the summary diff says which files did, and only those
      // and their folders are expired. The PR panel is not refreshed here:
      // it polls while shown and refreshes on its own actions.
      void applySnapshotUpdated(queryClient, update, { localWatching: isLocallyWatched(update.worktreeId) });
      return;
    }
    case "pr.updated": {
      void queryClient.invalidateQueries({ queryKey: qk.prWorkspace(update.worktreeId) });
      void queryClient.invalidateQueries({ queryKey: qk.worktree(update.worktreeId) });
      if (update.status) {
        // A status-less update a moment ago may have started a refetch; its
        // older answer must not land on top of this authoritative one.
        void queryClient.cancelQueries({ queryKey: qk.pullRequestStatus(update.worktreeId) });
        queryClient.setQueryData(qk.pullRequestStatus(update.worktreeId), update.status);
      } else {
        void queryClient.invalidateQueries({ queryKey: qk.pullRequestStatus(update.worktreeId) });
      }
      return;
    }
  }
}

/**
 * The organization itself is gone for this member (a refused upgrade or
 * detail read), or the session is: what was read under it is cleared from
 * memory and disk, and the page reloads, which reports the refusal. Unsent
 * work is written down first; a session that merely expired keeps it for the
 * next sign-in, an organization the member left does not.
 */
function leaveInaccessibleOrg(signedOut: boolean) {
  if (useOrgSocket.getState().accessReloading) return;
  const orgId = currentOrgId;
  if (orgId) markAccessRefreshing(orgId);
  recordDiagnostic("access", signedOut ? "session refused; reloading" : `no access to ${orgId ?? "?"}; reloading`);
  prepareForReload({ forgetOrg: signedOut ? undefined : orgId ?? undefined });
  explicitClose = true;
  currentOrgId = null;
  clearTimers();
  const previous = ws;
  ws = null; // Ignore already-queued events from the old socket.
  detach(previous);
  subscriptions.clear();
  clearChatTailChecks();
  clearAllChatRepairs();
  lastTypingSentAt.clear();
  lastOrgRevision = null;
  previous?.close(4000, "access changed");
  // Unmount portals, previews and terminal emulators before clearing stores or
  // starting network work. Old mutation callbacks stay invisible until reload.
  flushSync(() => useOrgSocket.setState({ accessReloading: true, status: "closed", presenceByChat: {} }));
  terminalFrameListeners.clear();
  useScriptStore.getState().reset();
  const cleared = clearAccessScopedState(queryClient);
  // The reload waits (bounded) for the on-disk copy to be deleted: a delete
  // still in flight at unload is aborted, leaving records read under the old
  // access.
  void boundedClear(cleared).then(() => window.location.reload());
}

let accessReconcile: Promise<void> | null = null;
let accessReconcileAgain = false;

/**
 * `access.changed`, surgically (CONTRACT C4): the organization's lists are
 * read again and only what this member can no longer see goes — its cached
 * reads, its transcripts, its unsent drafts. The page, the selection and
 * every other composer stay. It used to clear everything and reload, which
 * lost unsent work on a teammate's team edit.
 */
export function reconcileAccessChange(orgId: string): Promise<void> {
  if (accessReconcile) {
    accessReconcileAgain = true;
    return accessReconcile;
  }
  accessReconcile = (async () => {
    try {
      do {
        accessReconcileAgain = false;
        await runAccessReconcile(orgId);
      } while (accessReconcileAgain && currentOrgId === orgId);
    } finally {
      accessReconcile = null;
    }
  })();
  return accessReconcile;
}

async function runAccessReconcile(orgId: string): Promise<void> {
  recordDiagnostic("access", `access changed in ${orgId}; reconciling in place`);
  try {
    await client.orgDetail(orgId);
  } catch (error) {
    if (currentOrgId !== orgId) return;
    if (error instanceof RivetsApiError && [401, 403, 404].includes(error.status)) {
      leaveInaccessibleOrg(error.status === 401);
      return;
    }
    // Anything else is not about access; the lists below say what they can.
  }
  if (currentOrgId !== orgId) return;
  const heldRepos = queryClient.getQueryData<{ repos: Repo[] }>(qk.repos(orgId))?.repos ?? [];
  const before = new Map<string, string>();
  for (const repo of heldRepos) {
    for (const worktree of queryClient.getQueryData<{ worktrees: Worktree[] }>(qk.worktrees(repo.id))?.worktrees ?? []) before.set(worktree.id, repo.id);
  }
  let repos: Repo[];
  try {
    repos = (await client.listRepos(orgId)).repos;
  } catch {
    if (currentOrgId === orgId) reconcileOrgQueries(orgId);
    return;
  }
  if (currentOrgId !== orgId) return;
  queryClient.setQueryData(qk.repos(orgId), { repos });
  const lists = await Promise.allSettled(repos.map((repo) => client.listWorktrees(repo.id)));
  if (currentOrgId !== orgId) return;
  const kept = new Set(repos.map((repo) => repo.id));
  const after = new Set<string>();
  const answered = new Set<string>();
  lists.forEach((result, index) => {
    const repoId = repos[index]!.id;
    if (result.status !== "fulfilled") return;
    answered.add(repoId);
    queryClient.setQueryData(qk.worktrees(repoId), result.value);
    for (const worktree of result.value.worktrees) after.add(worktree.id);
  });
  // Lost: every task of a repository that is gone, and every task missing
  // from its repository's fresh list. A list that failed to load proves
  // nothing, so its tasks stay.
  const lost: string[] = [];
  for (const [worktreeId, repoId] of before) {
    if (!kept.has(repoId) || (answered.has(repoId) && !after.has(worktreeId))) lost.push(worktreeId);
  }
  for (const repo of heldRepos) if (!kept.has(repo.id)) queryClient.removeQueries({ queryKey: qk.worktrees(repo.id) });
  const lostChats = lost.length > 0 ? forgetWorktrees(lost) : [];
  // The disk copies go by what they say about themselves, so chats of a lost
  // task this page never listed are found too (review #14). Only complete
  // fresh lists prove a task is gone that the cache did not hold.
  const accessible = answered.size === repos.length ? after : null;
  void forgetCachedChats(orgId, { chatIds: lostChats, worktreeIds: lost }, accessible).then((erased) => {
    for (const chatId of erased) {
      if (lostChats.includes(chatId)) continue;
      if (subscriptions.has(chatId)) useOrgSocket.getState().unsubscribeChat(chatId);
      queryClient.removeQueries({ queryKey: qk.chat(chatId) });
      useChatStore.getState().reset(chatId);
    }
  });
  reconcileOrgQueries(orgId);
}

/** Drops everything held for tasks this member can no longer see. Returns
 * the chats it knew of. */
function forgetWorktrees(worktreeIds: readonly string[]): string[] {
  recordDiagnostic("access", `forgetting ${worktreeIds.length} task(s) no longer visible`);
  const chatIds: string[] = [];
  for (const worktreeId of worktreeIds) {
    for (const chat of queryClient.getQueryData<WorktreeDetailResponse>(qk.worktree(worktreeId))?.chats ?? []) chatIds.push(chat.id);
    for (const key of [qk.worktree(worktreeId), qk.snapshot(worktreeId), qk.prWorkspace(worktreeId), qk.pullRequestStatus(worktreeId),
      qk.files(worktreeId), qk.dirs(worktreeId), qk.worktreeParticipants(worktreeId), qk.previewServices(worktreeId)]) {
      queryClient.removeQueries({ queryKey: key });
    }
  }
  for (const chatId of chatIds) {
    if (subscriptions.has(chatId)) useOrgSocket.getState().unsubscribeChat(chatId);
    queryClient.removeQueries({ queryKey: qk.chat(chatId) });
    queryClient.removeQueries({ queryKey: qk.chatApproval(chatId) });
    useChatStore.getState().reset(chatId);
  }
  forgetDraftsForChats(chatIds, worktreeIds);
  return chatIds;
}

/** An `org.update` this build could not read: its revision is still spent,
 * and what it said is reread as for a gap. */
function handleUnreadableOrgUpdate(revision: number) {
  if (lastOrgRevision !== null && revision <= lastOrgRevision) return;
  lastOrgRevision = revision;
  if (currentOrgId) reconcileOrgQueries(currentOrgId);
  for (const chatId of subscriptions) sendSubscribe(chatId);
}

function cachedWorkerOnline(orgId: string, workerId: string): boolean {
  return queryClient.getQueryData<WorkerListCache>(qk.workers(orgId))?.workers.some((worker) => worker.id === workerId && worker.status === "online") ?? false;
}

// -- protocol reload (CONTRACT C4) ---------------------------------------------

const UPDATE_RELOAD_KEY = "rivets.update-reload.v1";
/** A reload for the same expected version this recently means the reload did
 * not fix it (a stale cached bundle, a deploy mid-rollout): stop and ask. */
export const UPDATE_RELOAD_WINDOW_MS = 60_000;

/** Whether reloading for `expectedVersion` now would be a loop. */
export function reloadedRecentlyFor(expectedVersion: number, now = Date.now()): boolean {
  try {
    const stored = JSON.parse(sessionStorage.getItem(UPDATE_RELOAD_KEY) ?? "null") as { version?: unknown; at?: unknown } | null;
    return stored?.version === expectedVersion && typeof stored.at === "number" && now - stored.at < UPDATE_RELOAD_WINDOW_MS;
  } catch {
    return false;
  }
}

function noteUpdateReload(expectedVersion: number, now = Date.now()): boolean {
  try {
    sessionStorage.setItem(UPDATE_RELOAD_KEY, JSON.stringify({ version: expectedVersion, at: now }));
    return true;
  } catch {
    // Without storage there is no loop guard, so no automatic reload either.
    return false;
  }
}

/**
 * The server needs a newer client (web #1). Drafts and pending sends are
 * written down, then the page reloads once onto the new bundle. A reload for
 * the same version in the last minute, or unsaved file edits (which are not
 * persisted), leave the "Rivets was updated — Reload" strip instead.
 */
function reloadForUpdate(expectedVersion: number) {
  flushDrafts();
  if (hasUnsavedFileDrafts() || reloadedRecentlyFor(expectedVersion)) return;
  if (!noteUpdateReload(expectedVersion)) return;
  prepareForReload();
  window.location.reload();
}

/** The strip's Reload button: the reader asked, so no loop guard. */
export function reloadForUpdateNow(): void {
  prepareForReload();
  window.location.reload();
}

// A send whose outcome is unknown is settled by reading the chat back: a
// resubscribe from the cursor replays its echo if it landed.
setChatOwnerLookup((chatId) => {
  const chat = queryClient.getQueryData<import("@rivets/core").ChatDetailResponse>(qk.chat(chatId))?.chat;
  return chat ? { orgId: chat.orgId, worktreeId: chat.worktreeId } : undefined;
});

setPendingSendRecovery((chatId) => {
  if (subscriptions.has(chatId)) sendSubscribe(chatId);
});

registerDiagnosticSection("Relay", () => {
  const state = useOrgSocket.getState();
  const chats = useChatStore.getState().chats;
  const unsynced = [...subscriptions].filter((chatId) => chats[chatId]?.sync !== "live");
  return [
    `Organization: ${currentOrgId ?? "none"} — socket ${state.status}`,
    `Server: ${state.protocol ? `protocol ${state.protocol.version} (${state.protocol.serverGeneration})` : state.protocolError ?? "not negotiated"}`,
    `Org revision: ${lastOrgRevision ?? "none"}; reconnect attempt ${attempt}`,
    `Subscribed chats: ${subscriptions.size}; unsynced: ${unsynced.join(", ") || "none"}`,
    `Pending sends: ${Object.values(usePendingSends.getState().byChat).flat().map((send) => `${send.clientRequestId} ${send.state}`).join(", ") || "none"}`,
    `Open composers: ${Object.keys(useChatComposers.getState().composers).length}`,
  ].join("\n");
});
